import { flow, Instance, types } from 'mobx-state-tree'
import '../../ieConfig/MobxConfig'
import { checkEligibilityApi, getEligibilityApiReq, SUBTYPES } from '../../network/api/checkEligibilityApi'
import { getOtpRequestBody, userAuthApi, validateCustomerOtpBody } from '../../network/api/customerOtpApi'
import { MessageTypes } from '../../utils'
import { RootStore } from '../store/Root'

const CustomerSession = types.model('CustomerSession', {
  userAccessToken: types.optional(types.string, ''),
  userRefreshToken: types.optional(types.string, ''),
  userAccessTokenExpiry: types.optional(types.number, -1),
  userRefreshTokenExpiry: types.optional(types.number, -1),
  userIdentifierToken: types.optional(types.string, ''),
  CHANNEL_ID: types.optional(types.string, ''),
})

const JioFinanceHubEmi = types.model('JioFinanceHubEmi', {
  bank: types.optional(types.string, ''),
  tenure: types.maybeNull(types.string),
  scheme: types.maybeNull(types.string),
  interestRate: types.maybeNull(types.string),
  discount: types.maybeNull(types.string),
  cashBack: types.maybeNull(types.string),
  metadata: types.optional(types.string, '{}'),
  processingEntityId: types.maybeNull(types.string),
  processingEntitySchemeId: types.maybeNull(types.string),
  processingMethod: types.maybeNull(types.string),
  schemeSubType: types.maybeNull(types.string),
  schemeType: types.maybeNull(types.string),
})

export const JioFinanceHub = types.model('JioFinanceHub', {
  mobileNo: types.optional(types.string, ''),
  metadata: types.optional(types.string, '{}'),
  subType: types.optional(types.string, ''),
  eligibilityResponseID: types.optional(types.string, ''),
  routingId: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  emi: JioFinanceHubEmi,
})
export const Validations = types.model('Validations', {
  validationPattern: types.optional(types.string, ''),
  errorMessage: types.optional(types.string, ''),
  label: types.optional(types.string, ''),
})

const MetaData = types.model('MetaData', {
  name: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  info: types.optional(types.string, ''),
  displayOrder: types.optional(types.string, ''),
  label: types.optional(types.string, ''),
  usage: types.optional(types.string, ''),
  validations: types.array(Validations),
})
const EMI = types.model('EMI', {
  bankId: types.optional(types.string, ''),
  bankName: types.maybeNull(types.string),
  scheme: types.maybeNull(types.string),
  tenure: types.maybeNull(types.string),
  value: types.maybeNull(types.string),
  totalAmount: types.maybeNull(types.string),
  interestRate: types.maybeNull(types.string),
  interest: types.maybeNull(types.string),
  discount: types.maybeNull(types.string),
  product: types.maybeNull(types.string),
  processingFee: types.maybeNull(types.string),
  processingFeeTax: types.maybeNull(types.string),
  convenienceFee: types.maybeNull(types.string),
  convenienceFeeTax: types.maybeNull(types.string),
  cashback: types.maybeNull(types.string),
  termsAndConditions: types.maybeNull(types.string),
  programType: types.maybeNull(types.string),
  metadata: types.array(MetaData),
  additionalCashback: types.maybeNull(types.string),
  allowZeroCost: types.maybeNull(types.string),
  productDiscountPercentage: types.maybeNull(types.string),
  productDiscount: types.maybeNull(types.string),
  subventionCashbackDiscount: types.maybeNull(types.string),
  jfsProcessingFee: types.maybeNull(types.string),
  validationKey: types.maybeNull(types.string),
  validationValue: types.maybeNull(types.string),
  schemeType: types.maybeNull(types.string),
  schemeSubType: types.maybeNull(types.string),
  processingEntitySchemeId: types.maybeNull(types.string),
  processingEntityId: types.maybeNull(types.string),
  processingMethod: types.maybeNull(types.string),
  routingId: types.maybeNull(types.string),
})

export const BanksWithEmiPlans = types.model('BanksWithEmiPlans', {
  bankId: types.optional(types.string, ''),
  bankName: types.optional(types.string, ''),
  planSource: types.optional(types.string, ''),
  emiPlans: types.optional(types.array(EMI), []),
})

const BanksRequiresExtraInfo = types.model('BanksRequiresExtraInfo', {
  bankId: types.optional(types.string, ''),
  bankName: types.optional(types.string, ''),
  metadata: types.optional(types.array(MetaData), []),
})
const JioPayFinanceHub = types
  .model('JioPayFinanceHub', {
    customerSession: types.optional(CustomerSession, {}),
    banksWithEmiPlans: types.array(BanksWithEmiPlans),
    banksRequiresExtraInfoList: types.array(BanksRequiresExtraInfo),
    amountPayable: types.optional(types.string, '0'),
    mobileNo: types.optional(types.string, ''),
    eligibilityResponseID: types.optional(types.string, ''),
    routingId: types.optional(types.string, ''),
    type: types.optional(types.string, ''),
    subType: types.optional(types.string, ''),
    bankRequireExtraInfoMetaData: types.optional(types.string, ''),
  })
  .actions((self) => ({
    setSubType: (subType: string) => {
      self.subType = subType
    },
    sendCustomerOTP: flow(function* sendCustomerOTP(mobileNo: string) {
      try {
        const req = getOtpRequestBody(mobileNo)
        yield userAuthApi(req)
      } catch (err) {
        RootStore.message.setMessage(err?.message, MessageTypes.error, err?.code)
        throw err
      }
    }),
    validateCustomerOTP: flow(function* validateCustomerOTP(mobileNo: string, otp: string) {
      try {
        const req = validateCustomerOtpBody(mobileNo, otp)
        const {
          session: { accessToken, refreshToken, identifierToken },
        } = yield userAuthApi(req)
        self.customerSession.userAccessToken = accessToken?.tokenValue
        self.customerSession.userRefreshToken = refreshToken?.tokenValue
        self.customerSession.userAccessTokenExpiry = accessToken?.expiresIn
        self.customerSession.userRefreshTokenExpiry = refreshToken?.expiresIn
        self.customerSession.userIdentifierToken = identifierToken
      } catch (err) {
        RootStore.message.setMessage(err?.message, MessageTypes.error, err?.code)
        throw err
      }
    }),
    checkEligibility: flow(function* checkEligibility(mobileNo: string, TYPE: string, bankId?: string, metaData?: any) {
      try {
        const req = getEligibilityApiReq(mobileNo, bankId, metaData, TYPE)
        const data = yield checkEligibilityApi(req, TYPE, bankId ? true : false)
        if (!bankId) {
          const { amountPayable, banksRequiedAdditionalInfo, banksEmiPlans, eligibilityResponseID } = data
          self.banksRequiresExtraInfoList = banksRequiedAdditionalInfo
          self.banksWithEmiPlans = banksEmiPlans
          self.amountPayable = amountPayable
          self.mobileNo = mobileNo
          self.eligibilityResponseID = eligibilityResponseID
        } else {
          self.bankRequireExtraInfoMetaData = JSON.stringify(metaData)
        }
        return data
      } catch (err) {
        console.log('err', err)
        RootStore.message.setMessage(err?.message, MessageTypes.info, err?.code)
        throw err
      }
    }),
    checkEligibilityWithOtpAsync: flow(function* checkEligibilityWithOtpAsync(
      mobileNo: string,
      otp: string,
      TYPE: string,
    ) {
      yield (self as any).validateCustomerOTP(mobileNo, otp)
      yield (self as any).checkEligibility(mobileNo, TYPE)
    }),
    clearBankWithExtraInfoMetaData: function clearBankWithExtraInfoMetaData() {
      self.bankRequireExtraInfoMetaData = ''
    },
    getSupportedInstrument: function getSupportedInstrument() {
      const savedCard: any = RootStore?.user?.paymentMethod.filter((card: any) => card?.type === 'CREDITCARD')
      const supportedInstruments = [
        {
          headerTitle: savedCard?.length > 0 ? 'MORE WAYS TO PAY' : '',
          data: [],
        },
      ]

      const allowedPaymentConfigs = RootStore.merchant.allowedPaymentConfigs.split('|')
      const allowedPaymentModes = RootStore.merchant.allowedPaymentModes.map((item: any) => item.methodSubType)
      const notAllowedPaymentModes = RootStore.merchant.notAllowedPaymentModes.map((item: any) => item.methodSubType)
      if (RootStore.merchant.preferences.EMILList.length > 0) {
        if (
          allowedPaymentConfigs.includes(SUBTYPES.creditCardEmi) &&
          (allowedPaymentModes.length === 0 || allowedPaymentModes.includes(SUBTYPES.creditCardEmi)) &&
          (notAllowedPaymentModes.length === 0 || !allowedPaymentModes.includes(SUBTYPES.creditCardEmi))
        ) {
          const ccEmiRank = RootStore?.merchant?.allowedPaymentModes?.find(
            (item: any) => item.methodSubType === SUBTYPES.creditCardEmi,
          )?.rank
          supportedInstruments[0].data.push({ rank: ccEmiRank, data: intrumentsData.ccEmi } as never)
        }
      }
      if (!RootStore.transaction.isOfferApplied) {
        if (
          allowedPaymentConfigs.includes(SUBTYPES.debitCardEmi) &&
          (allowedPaymentModes.length === 0 || allowedPaymentModes.includes(SUBTYPES.debitCardEmi)) &&
          (notAllowedPaymentModes.length === 0 || !allowedPaymentModes.includes(SUBTYPES.debitCardEmi))
        ) {
          const dcEmiRank = RootStore?.merchant?.allowedPaymentModes?.find(
            (item: any) => item.methodSubType === SUBTYPES.debitCardEmi,
          )?.rank
          supportedInstruments[0].data.push({ rank: dcEmiRank, data: intrumentsData.dcEmi } as never)
        }
        if (
          allowedPaymentConfigs.includes(SUBTYPES.cardlessEmi) &&
          (allowedPaymentModes.length === 0 || allowedPaymentModes.includes(SUBTYPES.cardlessEmi)) &&
          (notAllowedPaymentModes.length === 0 || !allowedPaymentModes.includes(SUBTYPES.cardlessEmi))
        ) {
          const cardlessEmiRank = RootStore?.merchant?.allowedPaymentModes?.find(
            (item: any) => item.methodSubType === SUBTYPES.cardlessEmi,
          )?.rank
          supportedInstruments[0].data.push({ data: intrumentsData.cardlessEmi, rank: cardlessEmiRank } as never)
        }

        if (
          allowedPaymentConfigs.includes(SUBTYPES.payLaterEmi) &&
          (allowedPaymentModes.length === 0 || allowedPaymentModes.includes(SUBTYPES.payLaterEmi)) &&
          (notAllowedPaymentModes.length === 0 || !allowedPaymentModes.includes(SUBTYPES.payLaterEmi))
        ) {
          const paylaterRank = RootStore?.merchant?.allowedPaymentModes?.find(
            (item: any) => item.methodSubType === SUBTYPES.payLaterEmi,
          )?.rank
          supportedInstruments[0].data.push({ rank: paylaterRank, data: intrumentsData.payLaterEmi } as never)
        }
      }

      supportedInstruments[0].data?.sort((item1: any, item2: any) => {
        if (isNaN(Number(item1.rank))) {
          return 1
        } else if (isNaN(Number(item2.rank))) {
          return -1
        } else if (item1?.rank.trim() !== '') {
          if (item2?.rank.trim() !== '') {
            return item1.rank - item2.rank
          } else {
            return -1
          }
        } else {
          return 1
        }
      })
      const cardTypes = supportedInstruments[0]?.data?.map((item: any) => item.data)
      supportedInstruments[0].data = cardTypes as never
      return supportedInstruments
    },
    isCreditCardEmiAvailable: function isCreditCardEmiAvailable() {
      const allowedPaymentConfigs = RootStore.merchant.allowedPaymentConfigs.split('|')
      const allowedPaymentModes = RootStore.merchant.allowedPaymentModes.map((item: any) => item.methodSubType)
      const notAllowedPaymentModes = RootStore.merchant.notAllowedPaymentModes.map((item: any) => item.methodSubType)
      if (
        allowedPaymentConfigs.includes(SUBTYPES.creditCardEmi) &&
        (allowedPaymentModes.length === 0 || allowedPaymentModes.includes(SUBTYPES.creditCardEmi)) &&
        (notAllowedPaymentModes.length === 0 || !allowedPaymentModes.includes(SUBTYPES.creditCardEmi))
      ) {
        return true
      }

      return false
    },
  }))

const JioPayFinanceHubInitialData = JioPayFinanceHub.create({})

export type JioPayFinanceHubType = Instance<typeof JioPayFinanceHub>
export { JioPayFinanceHub, JioPayFinanceHubInitialData }
export type JioFinanceHubType = Instance<typeof JioFinanceHub>

export const intrumentsData = {
  ccEmi: {
    title: 'Credit Card EMI',
    description: 'HDFC, ICICI, SBI and more',
    type: 'credit_card',
    icon: 'DEFAULT_CARD',
  },
  dcEmi: {
    title: 'Debit Card EMI',
    description: 'HDFC, ICICI, SBI and more',
    type: 'debitCardEmi',
    icon: 'DEFAULT_CARD',
  },
  cardlessEmi: {
    title: 'Cardless EMI',
    description: 'HDFC, ICICI, SBI and more',
    type: 'cardlessEmi',
    icon: 'PAYLATER',
  },
  payLaterEmi: {
    title: 'Buy Now Pay Later EMI',
    description: 'ZestMoney, OlaPostpaid and more',
    type: 'payLaterEmi',
    icon: 'PAYLATER',
  },
}
